import React, { useEffect, useMemo, useRef } from 'react';
import { CandlestickSeries, ColorType, createChart, CrosshairMode, HistogramSeries, LineSeries, LineStyle } from 'lightweight-charts';
import { observeElementResize } from '../../utils/observeElementResize';
import { TradeExecutionMarkersPrimitive } from '../../components/StockChart/tradeExecutionMarkersPrimitive';
import {
  ExecutionLabelsPrimitive,
  FvgBoxesPrimitive,
  PlayheadPrimitive,
  SessionBoxesPrimitive,
  TradeBracketPrimitive,
  fvgBoxesUpToPlayhead,
  sessionBoxesUpToPlayhead,
  executionLabelsFromTrades,
  buildTradeBracket,
} from './backtestChartPrimitives';
import BacktestChartLegend from './BacktestChartLegend';
import { chartLegendItems, hasFvgBoxes, hasSessionBoxes } from '../chartOverlays';
import { formatChartCrosshair, formatChartTick } from './format';

function overlayColor(overlay, colors) {
  return overlay.color || colors[overlay.colorKey] || colors.primary;
}

function lineOptions(overlay, colors) {
  return {
    color: overlayColor(overlay, colors),
    lineWidth: overlay.lineWidth || 1,
    lineStyle: overlay.lineStyle === 'dashed' ? LineStyle.Dashed : LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
    crosshairMarkerVisible: false,
    title: '',
  };
}

export default function BacktestChart({
  bars = [],
  playhead = 0,
  indicators = {},
  overlays = [],
  trades = [],
  selectedTrade = null,
  openPosition = null,
  colors,
  onPlayhead,
}) {
  const wrapRef = useRef(null);
  const chartRef = useRef(null);
  const barsRef = useRef(bars);
  barsRef.current = bars;
  const onPlayheadRef = useRef(onPlayhead);
  onPlayheadRef.current = onPlayhead;
  const candleRef = useRef(null);
  const volumeRef = useRef(null);
  const overlaySeriesRef = useRef(new Map());
  const markersRef = useRef(null);
  const labelsRef = useRef(null);
  const fvgRef = useRef(null);
  const sessionRef = useRef(null);
  const playheadRef = useRef(null);
  const bracketRef = useRef(null);
  const lastRenderedBarsRef = useRef(null);
  const lastRenderedPlayheadRef = useRef(-1);

  const legendItems = useMemo(
    () => chartLegendItems(overlays.map((overlay) => ({
      ...overlay,
      color: overlayColor(overlay, colors),
      values: (overlay.values || []).slice(0, playhead + 1),
    })), {
      showFvg: hasFvgBoxes((indicators.fvg || []).slice(0, playhead + 1)),
      showSession: hasSessionBoxes((indicators.sessionBoxes || []).filter((b) => b.startIndex <= playhead)),
      colors,
    }),
    [overlays, indicators.fvg, indicators.sessionBoxes, playhead, colors],
  );

  useEffect(() => {
    if (!wrapRef.current) return undefined;
    const chart = createChart(wrapRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: colors.bgPrimary || colors.bgCard },
        textColor: colors.textMuted,
        fontFamily: 'inherit',
      },
      grid: {
        vertLines: { color: `${colors.border}90` },
        horzLines: { color: `${colors.border}60` },
      },
      rightPriceScale: { borderColor: colors.border, scaleMargins: { top: 0.06, bottom: 0.18 } },
      timeScale: {
        borderColor: colors.border,
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 4,
        barSpacing: 7,
        tickMarkFormatter: (time, tickMarkType) => formatChartTick(time, tickMarkType),
      },
      localization: {
        timeFormatter: (time) => formatChartCrosshair(time),
      },
      crosshair: { mode: CrosshairMode.Normal },
      width: wrapRef.current.clientWidth,
      height: wrapRef.current.clientHeight || 320,
      handleScroll: true,
      handleScale: true,
    });
    const candle = chart.addSeries(CandlestickSeries, {
      upColor: colors.success,
      downColor: colors.danger,
      borderUpColor: colors.success,
      borderDownColor: colors.danger,
      wickUpColor: colors.success,
      wickDownColor: colors.danger,
    });
    const volume = chart.addSeries(HistogramSeries, {
      priceFormat: { type: 'volume' },
      priceScaleId: '',
      lastValueVisible: false,
      priceLineVisible: false,
    });
    volume.priceScale().applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
    const markers = new TradeExecutionMarkersPrimitive();
    const labels = new ExecutionLabelsPrimitive();
    const session = new SessionBoxesPrimitive();
    const fvg = new FvgBoxesPrimitive();
    const playheadLine = new PlayheadPrimitive(colors.primary);
    const bracket = new TradeBracketPrimitive();
    candle.attachPrimitive(session);
    candle.attachPrimitive(fvg);
    candle.attachPrimitive(markers);
    candle.attachPrimitive(labels);
    candle.attachPrimitive(playheadLine);
    candle.attachPrimitive(bracket);
    chartRef.current = chart;
    candleRef.current = candle;
    volumeRef.current = volume;
    markersRef.current = markers;
    labelsRef.current = labels;
    sessionRef.current = session;
    fvgRef.current = fvg;
    playheadRef.current = playheadLine;
    bracketRef.current = bracket;

    chart.subscribeClick((param) => {
      if (!param?.time || !onPlayheadRef.current) return;
      const idx = barsRef.current.findIndex((b) => b.time === param.time);
      if (idx >= 0) onPlayheadRef.current(idx);
    });

    const disconnect = observeElementResize(wrapRef.current, () => {
      if (!wrapRef.current || !chartRef.current) return;
      chartRef.current.applyOptions({
        width: wrapRef.current.clientWidth,
        height: wrapRef.current.clientHeight,
      });
    });

    return () => {
      disconnect();
      overlaySeriesRef.current.clear();
      try { candle.detachPrimitive(session); } catch (_) { /* */ }
      try { candle.detachPrimitive(fvg); } catch (_) { /* */ }
      try { candle.detachPrimitive(markers); } catch (_) { /* */ }
      try { candle.detachPrimitive(labels); } catch (_) { /* */ }
      try { candle.detachPrimitive(playheadLine); } catch (_) { /* */ }
      try { candle.detachPrimitive(bracket); } catch (_) { /* */ }
      chart.remove();
      chartRef.current = null;
    };
    // Chart is recreated on color change only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colors.bgPrimary, colors.bgCard, colors.border, colors.success, colors.danger, colors.textMuted, colors.primary]);

  useEffect(() => {
    const candle = candleRef.current;
    const chart = chartRef.current;
    if (!candle || !chart) return;
    const toPoint = (b) => ({ time: b.time, open: b.open, high: b.high, low: b.low, close: b.close });
    const toVolumePoint = (b) => ({
      time: b.time,
      value: Math.max(0, b.volume || 0),
      color: b.close >= b.open ? `${colors.success}66` : `${colors.danger}66`,
    });
    const toLinePoint = (series, b, i) => {
      const v = series?.[b.index] ?? series?.[i];
      return Number.isFinite(v) ? { time: b.time, value: v } : null;
    };

    const seriesMap = overlaySeriesRef.current;
    const keep = new Set(overlays.map((overlay) => overlay.id));
    for (const [id, series] of seriesMap) {
      if (!keep.has(id)) {
        try { chart.removeSeries(series); } catch (_) { /* */ }
        seriesMap.delete(id);
      }
    }
    const overlaySeriesList = overlays.map((overlay) => {
      let series = seriesMap.get(overlay.id);
      if (!series) {
        series = chart.addSeries(LineSeries, lineOptions(overlay, colors));
        seriesMap.set(overlay.id, series);
      } else {
        series.applyOptions(lineOptions(overlay, colors));
      }
      return { overlay, series };
    });

    // When the bars array is unchanged and the playhead advanced by exactly one
    // bar (the common case during backtest playback), push a single incremental
    // update to each series instead of re-slicing and re-ingesting the whole
    // visible range every tick.
    const canIncrement = lastRenderedBarsRef.current === bars
      && playhead === lastRenderedPlayheadRef.current + 1
      && playhead > 0
      && playhead < bars.length;

    if (canIncrement) {
      const newBar = bars[playhead];
      candle.update(toPoint(newBar));
      volumeRef.current?.update(toVolumePoint(newBar));
      for (const { overlay, series } of overlaySeriesList) {
        const point = toLinePoint(overlay.values, newBar, playhead);
        if (point) series.update(point);
      }
    } else {
      const visible = bars.slice(0, Math.max(1, playhead + 1));
      candle.setData(visible.map(toPoint));
      volumeRef.current?.setData(visible.map(toVolumePoint));
      for (const { overlay, series } of overlaySeriesList) {
        series.setData(visible.map((b, i) => toLinePoint(overlay.values, b, i)).filter(Boolean));
      }
    }
    lastRenderedBarsRef.current = bars;
    lastRenderedPlayheadRef.current = playhead;

    sessionRef.current?.setBoxes(
      sessionBoxesUpToPlayhead(indicators.sessionBoxes, playhead, bars),
      colors.primary || '#818CF8',
    );
    fvgRef.current?.setBoxes(fvgBoxesUpToPlayhead(indicators.fvg, playhead));
    const playBar = bars[Math.min(playhead, bars.length - 1)];
    playheadRef.current?.setPlayhead(playBar?.time, colors.primary);
  }, [bars, playhead, overlays, indicators.fvg, indicators.sessionBoxes, colors]);

  useEffect(() => {
    const candle = candleRef.current;
    if (!candle || !markersRef.current) return;
    const visibleTrades = trades.filter((t) => t.entryIndex <= playhead);
    const markers = [];
    for (const t of visibleTrades) {
      const entryBar = bars[t.entryIndex];
      if (entryBar) {
        markers.push({
          time: entryBar.time,
          price: t.entryPrice,
          color: t.side === 'long' ? colors.success : colors.danger,
          side: t.side === 'long' ? 'buy' : 'sell',
          text: t.side === 'long' ? 'B' : 'S',
          id: `e-${t.id}`,
        });
      }
    }
    markersRef.current.setMarkers(markers);
    labelsRef.current?.setLabels(
      executionLabelsFromTrades(visibleTrades, bars, playhead, colors),
      colors.bgPrimary || colors.bgCard,
    );

    const focus = selectedTrade || openPosition;
    bracketRef.current?.setBracket(buildTradeBracket(focus, playhead), {
      success: colors.success,
      danger: colors.danger,
      text: colors.text,
      textMuted: colors.textMuted,
      bg: colors.bgPrimary || colors.bgCard,
    });
  }, [bars, playhead, trades, selectedTrade, openPosition, colors]);

  useEffect(() => {
    if (!chartRef.current || !bars.length) return;
    const focus = selectedTrade || openPosition;
    const entry = focus?.entryIndex;
    const exit = Number.isFinite(focus?.exitIndex) ? focus.exitIndex : playhead;
    const from = Number.isFinite(entry)
      ? Math.max(0, Math.min(playhead - 80, entry - 8))
      : Math.max(0, playhead - 80);
    const to = Math.max(playhead, exit) + 8;
    try {
      chartRef.current.timeScale().setVisibleLogicalRange({ from, to });
    } catch (_) { /* range may be empty on first paint */ }
  }, [playhead, bars.length, selectedTrade, openPosition]);

  return (
    <div
      data-testid="backtest-chart"
      ref={wrapRef}
      style={{ width: '100%', height: '100%', minHeight: 220, position: 'relative' }}
    >
      <BacktestChartLegend items={legendItems} colors={colors} />
    </div>
  );
}
